import React, { useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';

import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';

import Skeleton from '@mui/material/Skeleton';
import { Box } from '@mui/material';
import Item from '@mui/material/Grid';

const UkraineTrip = () => {
    const { accounts } = useMsal();
    const gameContainerRef = useRef(null);
    
    // Actual itinerary data based on flight information
    const itineraryItems = [
        {
            day: "Day 1",
            date: "Saturday, August 2, 2025",
            title: "Travel to Ukraine",
            activities: [
                { time: "6:00", description: "Depart from Bristol Bus Station", icon: "location" },
                { time: "8:30", description: "Arrive at London Heathrow Airport", icon: "location" },
                { time: "10:15", description: "Flight LO282: London to Warsaw", icon: "flight" },
                { time: "14:40", description: "Flight LO515: Warsaw to Chisinau", icon: "flight" },
                { time: "17:30", description: "Arrive in Chisinau, Moldova", icon: "location" },
                { 
                    time: "~18:30", 
                    description: "Take bus to Odesa", 
                    icon: "bus",
                    warning: "Important: Must arrive in Ukraine before 11pm curfew"
                },
                { time: "22:00", description: "Check-in at airbnb", icon: "hotel" }
            ]
        },
        {
            day: "Days 2",
            date: "Sunday, August 3, 2025",
            title: "Travel to Mykolaiv",
            activities: [
                { time: "TBC", description: "Take bus from Odesa Central Bus Station", icon: "bus" },
                { time: "TBC", description: "Arrive in Mykolaiv", icon: "hotel" },
            ]
        },
        {
            date: "Friday, August 15th, 2025",
            title: "Return to Odesa",
            activities: [
                { time: "11:00", description: "Take bus to Odesa", icon: "bus" },
                { time: "12:30", description: "Arrive in Odesa, check-in at airbnb", icon: "hotel" },

            ]
        },
        {
            day: "Day 15",
            date: "Saturday, August 16, 2025",
            title: "Return Journey",
            activities: [
                { time: "10:00", description: "Check out of airbnb in Odesa", icon: "hotel" },
                { time: "11:00", description: "Take bus to Chisinau", icon: "bus" },
                { time: "18:15", description: "Flight LO516: Chisinau to Warsaw", icon: "flight" },
                { time: "20:20", description: "Flight LO285: Warsaw to London", icon: "flight" },
                { time: "22:00", description: "Arrive back in London", icon: "location" },
                { time: "22:30", description: "Take bus to Bristol", icon: "bus" },
                { time: "01:00", description: "Arrive back in Bristol", icon: "location" }
            ]
        }
    ];
    
    // Flight details for reference
    const flightDetails = {
        outbound: [
            {
                flight: "LO282",
                from: "London",
                to: "Warsaw",
                departure: "10:15, August 2, 2025",
                bookingRef: "CO5VBB",
                eTicket: ["080-7295956267", "080-7295956266"]
            },
            {
                flight: "LO515",
                from: "Warsaw",
                to: "Chisinau",
                departure: "14:40, August 2, 2025",
                bookingRef: "CO5VBB",
                eTicket: ["080-7295956267", "080-7295956266"]
            }
        ],
        return: [
            {
                flight: "LO516",
                from: "Chisinau",
                to: "Warsaw",
                departure: "18:15, August 16, 2025",
                bookingRef: "CO5VBB",
                eTicket: ["080-7295956267", "080-7295956266"]
            },
            {
                flight: "LO285",
                from: "Warsaw",
                to: "London",
                departure: "20:20, August 16, 2025",
                bookingRef: "CO5VBB",
                eTicket: ["080-7295956267", "080-7295956266"]
            }
        ]
    };
    
    // Flight details for the flight tracker
    const flightsToTrack = [
        {
            flightNumber: "LO282",
            date: "2025-08-02T10:15:00", // ISO format for the flight date
            route: "London to Warsaw"
        },
        {
            flightNumber: "LO515",
            date: "2025-08-02T14:40:00",
            route: "Warsaw to Chisinau"
        },
        {
            flightNumber: "LO516",
            date: "2025-08-16T18:15:00",
            route: "Chisinau to Warsaw"
        },
        {
            flightNumber: "LO285",
            date: "2025-08-16T20:20:00",
            route: "Warsaw to London"
        }
    ];
    
    // Function to render the appropriate icon label instead of icon components
    const getActivityLabel = (iconType) => {
        switch(iconType) {
            case 'location':
                return '📍';
            case 'hotel':
                return '🏨';
            case 'flight':
                return '✈️';
            case 'restaurant':
                return '🍽️';
            case 'walk':
                return '🚶';
            default:
                return '📌';
        }
    };
    
    return (
        <>
            <Box sx={{ padding: 2, maxWidth: 1200, margin: 'auto' }}>
                <Paper elevation={3} sx={{ padding: 3, marginBottom: 2 }}>
                    <Stack spacing={2}>
          
                         <h1>Ukraine Trip</h1>
                        <p>Still in progress. Text me if you have any ideas.</p>
                    </Stack>
                </Paper>
                
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                    <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', md: '70%' } }}>
                        <Paper elevation={3} sx={{ padding: 3, marginBottom: 2 }}>
                            <Stack spacing={2}>
                                <h2>Map</h2>
                                <iframe 
                                    src="https://www.google.com/maps/d/u/0/embed?mid=1f1uC-gkB7fNes3JLaLDS2-rkVb3OatU&ehbc=2E312F" 
                                    title="Map"
                                    width="100%" 
                                    height="600"
                                    style={{ border: 'none' }}
                                    allowFullScreen
                                />
                            </Stack>
                        </Paper>
                    </Box>
                    
                    <Box sx={{ flexBasis: { xs: '100%', md: '30%' } }}>
                        <Paper elevation={3} sx={{ 
                            padding: 3, 
                            marginBottom: 2,
                            height: { md: 'calc(100% - 16px)' },
                            overflowY: 'auto',
                            maxHeight: { md: '670px' }
                        }}>
                            <Stack spacing={2}>
                                <Typography variant="h5" component="h2">Travel Itinerary</Typography>
                                
                                <Box sx={{ overflowY: 'auto' }}>
                                    {itineraryItems.map((day, index) => (
                                        <Box key={index} sx={{ mb: 3 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <Chip 
                                                    label={day.day} 
                                                    color="primary" 
                                                    size="small" 
                                                    sx={{ mr: 1 }} 
                                                />
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    {day.title} <Typography component="span" variant="body2" color="text.secondary">({day.date})</Typography>
                                                </Typography>
                                            </Box>
                                            
                                            <Timeline position="right" sx={{ 
                                                p: 0, 
                                                m: 0,
                                                [`& .MuiTimelineItem-root`]: { 
                                                    minHeight: 'auto',
                                                    '&:before': { display: 'none' }
                                                }
                                            }}>
                                                {day.activities.map((activity, actIndex) => (
                                                    <TimelineItem key={actIndex}>
                                                        <TimelineOppositeContent sx={{ flex: 0.2, minWidth: '60px', py: 1, px: 0 }}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {activity.time}
                                                            </Typography>
                                                        </TimelineOppositeContent>
                                                        <TimelineSeparator>
                                                            <TimelineDot 
                                                                color={activity.warning ? "error" : "primary"} 
                                                                variant={activity.warning ? "filled" : "outlined"} 
                                                                sx={{ p: 0.5 }}
                                                            >
                                                                <Typography variant="body2">{getActivityLabel(activity.icon)}</Typography>
                                                            </TimelineDot>
                                                            {actIndex < day.activities.length - 1 && <TimelineConnector />}
                                                        </TimelineSeparator>
                                                        <TimelineContent sx={{ py: 1 }}>
                                                            <Typography variant="body2">
                                                                {activity.description}
                                                            </Typography>
                                                            {activity.warning && (
                                                                <Typography 
                                                                    variant="caption" 
                                                                    sx={{ 
                                                                        color: 'error.main', 
                                                                        display: 'block',
                                                                        mt: 0.5,
                                                                        fontWeight: 'bold'
                                                                    }}
                                                                >
                                                                    ⚠️ {activity.warning}
                                                                </Typography>
                                                            )}
                                                        </TimelineContent>
                                                    </TimelineItem>
                                                ))}
                                            </Timeline>
                                            
                                            {index < itineraryItems.length - 1 && <Divider sx={{ mt: 2 }} />}
                                        </Box>
                                    ))}
                                </Box>
                                
                                <Divider sx={{ my: 2 }} />
                                
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
                                    Flight Information
                                </Typography>
                                
                                <Typography variant="body2">
                                    <strong>Booking Reference:</strong> CO5VBB (LOT Polish Airlines)
                                </Typography>
                                
                                <Typography variant="body2">
                                    <strong>Passengers:</strong> Che Gamble, Kristina Kostomarova
                                </Typography>
                                
                                <Typography variant="body2">
                                    <strong>Baggage Allowance:</strong> 1 checked bag (23kg), 1 carry-on (8kg), 1 personal item (2kg)
                                </Typography>
                                
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Online check-in opens 48 hours before departure
                                </Typography>

                                <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" sx={{ color: 'error', fontWeight: 'bold' }}>
                                        ⚠️ Important Safety Information
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'error', mt: 1 }}>
                                        Ukraine has a nationwide curfew from 11:00 PM to 5:00 AM. Make sure all travel is completed before curfew begins.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Box>
                </Box>
                
            </Box>
        </>
    );
};

export default UkraineTrip;


